// Single source of truth for "which date can this session actually be ordered for?".
// Both Home (auto-switch on cutoff) and DateSessionSelector (card badges) call
// resolveSessionSlot, so the badge on a card and the slot Home jumps to can never disagree.

export const SESSION_ORDER = ["Breakfast", "Lunch", "Dinner"];

export const HORIZON_DAYS = 14;

const DAY_MS = 24 * 60 * 60 * 1000;

// All date keys are derived from LOCAL calendar parts, then frozen at UTC midnight.
// (Deriving them from getUTCDate() instead would put IST users on the previous day
// between 00:00 and 05:29 local.)
export const dateKey = (date) => {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

export const normalizeToUTCMidnight = (date) =>
  new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));

export const addDays = (dateObj, days) =>
  new Date(dateObj.getTime() + days * DAY_MS);

export const buildAvailabilityMap = (menuData = []) => {
  const map = {};
  menuData.forEach((item) => {
    if (!item?.deliveryDate || !item?.session) return;
    const key = dateKey(new Date(item.deliveryDate));
    if (!map[key]) map[key] = new Set();
    map[key].add(item.session);
  });
  return map;
};

export const hasMenu = (availability, dateObj, session) =>
  Boolean(dateObj && availability?.[dateKey(dateObj)]?.has(session));

// The cutoff that applies to this user. employeeCutoff is optional — when the
// backend omits it, employees fall back to the session's defaultCutoff.
export const getCutoff = (session, hubData, isEmployee) => {
  const times = hubData?.cutoffTimes?.[session?.toLowerCase()];
  if (!times) return null;
  const raw = isEmployee
    ? times.employeeCutoff || times.defaultCutoff
    : times.defaultCutoff;
  return raw || null;
};

export const formatCutoff = (timeStr) => {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
};

// Cutoffs only ever gate TODAY's menu — a future date is orderable all day.
export const isPastCutoff = (session, now, hubData, isEmployee) => {
  const cutoffStr = getCutoff(session, hubData, isEmployee);
  if (!cutoffStr) return false;
  const [h, m] = cutoffStr.split(":").map(Number);
  const cutoff = new Date(now);
  cutoff.setHours(h, m, 0, 0);
  return now >= cutoff;
};

// Post-cutoff grace window — instant-mode hubs only, today only. Time check only.
export const isWindowOpen = (session, now, hubData) => {
  if ((hubData?.orderMode || "preorder") !== "instant") return false;
  const times = hubData?.cutoffTimes?.[session?.toLowerCase()];
  if (!times?.windowEnabled || !times?.windowEnd) return false;
  const IST_MS = 5.5 * 60 * 60 * 1000;
  const ist = new Date(now.getTime() + IST_MS);
  const nowMins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  const [ch, cm] = (times.defaultCutoff || "0:0").split(":").map(Number);
  const [wh, wm] = times.windowEnd.split(":").map(Number);
  return nowMins >= ch * 60 + cm && nowMins < wh * 60 + wm;
};

export const getWindowEnd = (session, hubData) =>
  hubData?.cutoffTimes?.[session?.toLowerCase()]?.windowEnd || null;

/**
 * Does this session's window actually have portions left to sell?
 * An open window with nothing in it is not a slot — it must fall through to the next date.
 *
 * Counts come from `cutoffTimes[session].windowItemCount` (backend) and can be overridden
 * per session by `windowItems` — Home passes the live count for the selected session, which
 * the qty socket keeps current as portions sell out.
 */
export const windowHasItems = (session, hubData, windowItems) => {
  const key = session?.toLowerCase();

  const live = windowItems?.[key];
  if (live !== undefined && live !== null) return Number(live) > 0;

  const times = hubData?.cutoffTimes?.[key];
  if (typeof times?.windowItemCount === "number") {
    return times.windowItemCount > 0;
  }
  if (typeof times?.hasWindowItems === "boolean") return times.hasWindowItems;

  // Backend hasn't sent a count — assume the window is live rather than hiding a
  // sellable slot. Home's live fetch still corrects the selected session.
  return true;
};

// Preorder hubs sell tomorrow onward — today is never orderable there (employees excepted).
export const earliestOffset = (hubData, isEmployee) =>
  (hubData?.orderMode || "preorder") === "preorder" && !isEmployee ? 1 : 0;

/**
 * The next date this session can actually be ordered for.
 * status: "open"   — orderable on dateObj
 *         "window" — today, past cutoff, but the grace window is still running
 *         "none"   — nothing orderable within the horizon (→ "Serving Soon")
 */
export const resolveSessionSlot = ({
  session,
  availability,
  hubData,
  isEmployee = false,
  now = new Date(),
  horizonDays = HORIZON_DAYS,
  windowItems = null,
}) => {
  const today = normalizeToUTCMidnight(now);
  const start = earliestOffset(hubData, isEmployee);

  for (let i = start; i <= horizonDays; i++) {
    const dateObj = addDays(today, i);
    if (!hasMenu(availability, dateObj, session)) continue;

    if (i === 0 && isPastCutoff(session, now, hubData, isEmployee)) {
      // A window only counts while it still has portions to sell — an open but
      // sold-out (or empty) window falls through to the next date.
      if (
        isWindowOpen(session, now, hubData) &&
        windowHasItems(session, hubData, windowItems)
      ) {
        return { status: "window", dateObj };
      }
      continue; // today is closed for this session — keep looking
    }
    return { status: "open", dateObj };
  }

  return { status: "none", dateObj: null };
};

// "Today" / "Tomorrow" / "Thu" — cards beyond tomorrow show the weekday.
export const dayLabel = (dateObj, now = new Date()) => {
  if (!dateObj) return "";
  const today = normalizeToUTCMidnight(now);
  const key = dateKey(dateObj);
  if (key === dateKey(today)) return "Today";
  if (key === dateKey(addDays(today, 1))) return "Tomorrow";
  return dateObj.toLocaleDateString(undefined, {
    weekday: "long",
    timeZone: "UTC",
  });
};
